// Mindfulness Feature Utility Functions
// Helper functions to work with translations and constants

import { useEffect, useState } from "react";

/**
 * Get translated session category label
 * @param {string} category - Category key from SESSION_CATEGORIES
 * @param {function} t - Translation function from useTranslation
 * @returns {string} Translated category label
 * 
 * @example
 * import { useTranslation } from 'react-i18next';
 * import { SESSION_CATEGORIES } from './constants';
 * import { getSessionCategoryLabel } from './utils';
 * 
 * const { t } = useTranslation();
 * const label = getSessionCategoryLabel(SESSION_CATEGORIES.COUNSELING, t);
 */
export const getSessionCategoryLabel = (category, t) => {
  return t(`mindfulness.sessionCategories.${category}`);
};

/**
 * Get translated booking status label
 * @param {string} status - Status key from BOOKING_STATUS
 * @param {function} t - Translation function from useTranslation
 * @returns {string} Translated status label
 * 
 * @example
 * import { useTranslation } from 'react-i18next';
 * import { BOOKING_STATUS } from './constants';
 * import { getBookingStatusLabel } from './utils';
 * 
 * const { t } = useTranslation();
 * const label = getBookingStatusLabel(BOOKING_STATUS.PENDING, t);
 */
export const getBookingStatusLabel = (status, t) => {
  return t(`mindfulness.bookingStatus.${status}`);
};

/**
 * Get translated day of week label
 * @param {string} day - Day key from DAYS_OF_WEEK_LABELS
 * @param {function} t - Translation function from useTranslation
 * @returns {string} Translated day label
 * 
 * @example
 * import { useTranslation } from 'react-i18next';
 * import { DAYS_OF_WEEK_LABELS } from './constants';
 * import { getDayOfWeekLabel } from './utils';
 * 
 * const { t } = useTranslation();
 * const label = getDayOfWeekLabel(DAYS_OF_WEEK_LABELS[0], t); // "Sunday"
 */
export const getDayOfWeekLabel = (day, t) => {
  return t(`mindfulness.daysOfWeek.${day}`);
};

/**
 * Get translated validation message
 * @param {string} messageKey - Message key from VALIDATION_MESSAGES
 * @param {function} t - Translation function from useTranslation
 * @returns {string} Translated validation message
 * 
 * @example
 * import { useTranslation } from 'react-i18next';
 * import { VALIDATION_MESSAGES } from './constants';
 * import { getValidationMessage } from './utils';
 * 
 * const { t } = useTranslation();
 * const message = getValidationMessage(VALIDATION_MESSAGES.REQUIRED_FIELD, t);
 */
export const getValidationMessage = (messageKey, t) => {
  return t(`mindfulness.validationMessages.${messageKey}`);
};

/**
 * Get translated success message
 * @param {string} messageKey - Message key from SUCCESS_MESSAGES
 * @param {function} t - Translation function from useTranslation
 * @returns {string} Translated success message
 * 
 * @example
 * import { useTranslation } from 'react-i18next';
 * import { SUCCESS_MESSAGES } from './constants';
 * import { getSuccessMessage } from './utils';
 * 
 * const { t } = useTranslation();
 * const message = getSuccessMessage(SUCCESS_MESSAGES.SESSION_CREATED, t);
 */
export const getSuccessMessage = (messageKey, t) => {
  return t(`mindfulness.successMessages.${messageKey}`);
};

/**
 * Get translated error message
 * @param {string} messageKey - Message key from ERROR_MESSAGES
 * @param {function} t - Translation function from useTranslation
 * @returns {string} Translated error message
 * 
 * @example
 * import { useTranslation } from 'react-i18next';
 * import { ERROR_MESSAGES } from './constants';
 * import { getErrorMessage } from './utils';
 * 
 * const { t } = useTranslation();
 * const message = getErrorMessage(ERROR_MESSAGES.SESSION_NOT_FOUND, t);
 */
export const getErrorMessage = (messageKey, t) => {
  return t(`mindfulness.errorMessages.${messageKey}`);
};

/**
 * Get translated label
 * @param {string} labelKey - Label key (e.g., 'creatorSession', 'availability')
 * @param {function} t - Translation function from useTranslation
 * @returns {string} Translated label
 * 
 * @example
 * import { useTranslation } from 'react-i18next';
 * import { getLabel } from './utils';
 * 
 * const { t } = useTranslation();
 * const label = getLabel('creatorSession', t);
 */
export const getLabel = (labelKey, t) => {
  return t(`mindfulness.labels.${labelKey}`);
};

/**
 * Get translated placeholder
 * @param {string} placeholderKey - Placeholder key (e.g., 'enterSessionName')
 * @param {function} t - Translation function from useTranslation
 * @returns {string} Translated placeholder
 * 
 * @example
 * import { useTranslation } from 'react-i18next';
 * import { getPlaceholder } from './utils';
 * 
 * const { t } = useTranslation();
 * const placeholder = getPlaceholder('enterSessionName', t);
 */
export const getPlaceholder = (placeholderKey, t) => {
  return t(`mindfulness.placeholders.${placeholderKey}`);
};

/**
 * Get all translated session category options for dropdowns
 * @param {function} t - Translation function from useTranslation
 * @returns {Array} Array of {value, label} objects
 * 
 * @example
 * import { useTranslation } from 'react-i18next';
 * import { getSessionCategoryOptions } from './utils';
 * 
 * const { t } = useTranslation();
 * const options = getSessionCategoryOptions(t);
 * // [{ value: 'counseling', label: 'Counseling' }, ...]
 */
export const getSessionCategoryOptions = (t) => {
  return [
    { value: 'counseling', label: t('mindfulness.sessionCategories.counseling') },
    { value: 'meditation', label: t('mindfulness.sessionCategories.meditation') },
    { value: 'yoga', label: t('mindfulness.sessionCategories.yoga') },
    { value: 'therapy', label: t('mindfulness.sessionCategories.therapy') },
    { value: 'coaching', label: t('mindfulness.sessionCategories.coaching') },
  ];
};

/**
 * Get all translated booking status options for dropdowns
 * @param {function} t - Translation function from useTranslation
 * @returns {Array} Array of {value, label} objects
 * 
 * @example
 * import { useTranslation } from 'react-i18next';
 * import { getBookingStatusOptions } from './utils';
 * 
 * const { t } = useTranslation();
 * const options = getBookingStatusOptions(t);
 * // [{ value: 'pending', label: 'Pending' }, ...]
 */
export const getBookingStatusOptions = (t) => {
  return [
    { value: 'pending', label: t('mindfulness.bookingStatus.pending') },
    { value: 'confirmed', label: t('mindfulness.bookingStatus.confirmed') },
    { value: 'completed', label: t('mindfulness.bookingStatus.completed') },
    { value: 'cancelled', label: t('mindfulness.bookingStatus.cancelled') },
    { value: 'noShow', label: t('mindfulness.bookingStatus.noShow') },
  ];
};

/**
 * Get all translated day of week options for checkboxes/multi-select
 * @param {function} t - Translation function from useTranslation
 * @returns {Array} Array of {value, label} objects
 * 
 * @example
 * import { useTranslation } from 'react-i18next';
 * import { getDayOfWeekOptions } from './utils';
 * 
 * const { t } = useTranslation();
 * const options = getDayOfWeekOptions(t);
 * // [{ value: 0, label: 'Sunday' }, ...]
 */
export const getDayOfWeekOptions = (t) => {
  return [
    { value: 0, label: t('mindfulness.daysOfWeek.sunday') },
    { value: 1, label: t('mindfulness.daysOfWeek.monday') },
    { value: 2, label: t('mindfulness.daysOfWeek.tuesday') },
    { value: 3, label: t('mindfulness.daysOfWeek.wednesday') },
    { value: 4, label: t('mindfulness.daysOfWeek.thursday') },
    { value: 5, label: t('mindfulness.daysOfWeek.friday') },
    { value: 6, label: t('mindfulness.daysOfWeek.saturday') },
  ];
};

/**
 * Format duration in minutes to human readable format
 * @param {number} minutes - Duration in minutes
 * @param {function} t - Translation function from useTranslation
 * @returns {string} Formatted duration string
 * 
 * @example
 * import { useTranslation } from 'react-i18next';
 * import { formatDuration } from './utils';
 * 
 * const { t } = useTranslation();
 * const duration = formatDuration(90, t); // "1 Hours 30 Minutes"
 */
export const formatDuration = (minutes, t) => {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  
  if (hours > 0 && mins > 0) {
    return `${hours} ${t('mindfulness.labels.hours')} ${mins} ${t('mindfulness.labels.minutes')}`;
  } else if (hours > 0) {
    return `${hours} ${t('mindfulness.labels.hours')}`;
  } else {
    return `${mins} ${t('mindfulness.labels.minutes')}`;
  }
};


export const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}



